import { Modal, Spinner } from '@salesforce/design-system-react';

import { QualityFormulaStoreProvider } from '../../quality-formulas/store/QualityFormulaStoreProvider';
import { useTemplate } from '../hooks/useTemplates';
import { buildInitialWorkspaceState, TemplateEditorForm } from '../pages/TemplateEditorPage';

interface TemplateEditorContentProps {
  /** Forwarded to `TemplateEditorForm` — see its own doc comment. Only `TemplateManagerPanel`
   * needs this, since it's the one caller rendering this content inside a surface (its "Manage
   * Templates" card) that already has its own Cosmos heading. */
  compact?: boolean;
  onClose: () => void;
  /** `null` renders the form in create mode. */
  templateId: null | string;
}

/**
 * The loading-gated `TemplateEditorForm` body shared by `TemplateEditorModal` (a floating dialog)
 * and `TemplateManagerPanel`'s inline create/edit view (rendered in place inside the widget's own
 * card, with no separate dialog surface) — both need the same `useTemplate` fetch and
 * `QualityFormulaStoreProvider` seeding, just inside a different container.
 */
export const TemplateEditorContent: React.FC<TemplateEditorContentProps> = ({ compact, onClose, templateId }) => {
  const { data: template, isFetching, isLoading } = useTemplate(templateId);
  const isLoadingTemplate = templateId !== null && (isLoading || isFetching);
  const resolvedTemplate = templateId ? template : undefined;
  const initialState = buildInitialWorkspaceState(resolvedTemplate);

  if (isLoadingTemplate) {
    return (
      <div className="slds-grid slds-grid_align-center slds-p-around_x-large">
        <Spinner hasContainer={false} />
      </div>
    );
  }

  return (
    <QualityFormulaStoreProvider
      initialSpecStates={initialState.specStates}
      initialVisibleSpecTypes={initialState.visibleSpecTypes}
      key={templateId ?? 'new'}
    >
      <div className="slds-p-around_medium">
        <TemplateEditorForm compact={compact} onDone={onClose} template={resolvedTemplate} variant="embedded" />
      </div>
    </QualityFormulaStoreProvider>
  );
};

interface TemplateEditorModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** `null`/omitted opens the modal in create mode. */
  templateId?: null | string;
}

/**
 * Embeds `TemplateEditorContent` inside a `Modal` instead of navigating to a separate page — used
 * by the standalone Template Manager page, which has room for a floating dialog over its own list.
 * The Quality Adjustment workbench's inline Template Manager (`TemplateManagerPanel`) instead
 * swaps `TemplateEditorContent` directly into its own widget, since a contract line item's page
 * should never show a full-screen dialog over itself.
 */
export const TemplateEditorModal: React.FC<TemplateEditorModalProps> = ({ isOpen, onClose, templateId }) => {
  const resolvedId = templateId ?? null;

  return (
    <Modal
      assistiveText={{ dialogLabel: resolvedId ? 'Edit Template' : 'Create Template' }}
      dismissOnClickOutside={false}
      isOpen={isOpen}
      onRequestClose={onClose}
      size="large"
    >
      <TemplateEditorContent onClose={onClose} templateId={resolvedId} />
    </Modal>
  );
};
